import { z } from 'zod';
import { erroApiSchema, paginacaoSchema, paginadoSchema, vazioSchema, } from './schemas/comum.js';
import { atualizarUsuarioSchema, credencialClienteSchema, credencialEmitidaSchema, criarUsuarioSchema, definirCredencialClienteSchema, loginClienteSchema, sessaoClienteSchema, trocarSenhaPortalSchema, usuarioSchema, } from './schemas/auth.js';
import { atualizarClienteSchema, cerebroNegocioSchema, clienteSchema, criarClienteSchema, dadosNegocioSchema, filtroClientesSchema, importarClientesSchema, resultadoImportacaoSchema, salvarCerebroNegocioSchema, } from './schemas/cliente.js';
import { assetSchema, papelAssetSchema, criarHostnameSchema, criarSiteSchema, hostnameSchema, publicarSiteSchema, resolucaoHostnameSchema, resultadoPublicacaoSchema, siteSchema, siteSpecSchema, versaoSiteSchema, } from './schemas/site.js';
import { campanhaSchema, configJsonSchema, excluirLeadsSchema, filtroLeadsSchema, leadSchema, resultadoUploadSchema, salvarCampanhaSchema, salvarConfigJsonSchema, salvarLeadsSchema, uploadFotoLeadSchema, } from './schemas/captacao.js';
import { atualizarPagamentoSchema, configGlobalSchema, configPagamentoClienteSchema, criarFaturaSchema, criarPagamentoSchema, faturaSchema, filtroFaturasSchema, pagamentoSchema, pagarFaturaSchema, perfilConsumidorSchema, salvarConfigGlobalSchema, salvarConfigPagamentoSchema, salvarPerfilConsumidorSchema, } from './schemas/financeiro.js';
import { salvarVitrineSchema, vitrineSchema } from './schemas/vitrine.js';
import { aplicarPrecosSchema, catalogoPrecosSchema, configIaSchema, custoPorSiteSchema, diagnosticarIaSchema, diagnosticoIaSchema, filtroResumoCustoSchema, filtroUsoIaSchema, gerarComIaSchema, gerarSiteComIaSchema, listaModelosSchema, respostaGerarSiteSchema, respostaIaSchema, resultadoAplicarPrecosSchema, resumoCustoIaSchema, salvarConfigIaSchema, salvarPrecoModeloSchema, usoIaSchema, } from './schemas/ia.js';
import { alterarVendedorSchema, configComercialSchema, criarVendedorSchema, definirComissaoSchema, distribuirEmLoteSchema, distribuirLeadsSchema, fecharVendaSchema, filtroLeadsComerciaisSchema, filtroVendasSchema, identidadeComercialSchema, leadComercialDetalheSchema, leadComercialSchema, mudarStatusLeadSchema, paramsVendedorSchema, promoverLeadsCaptacaoSchema, registrarLigacaoSchema, registrarMensagemSchema, resultadoAlteracaoVendedorSchema, resultadoDistribuicaoLoteSchema, resultadoDistribuicaoSchema, resultadoPromocaoSchema, resumoLiderancaSchema, resumoVendedorSchema, vendaComercialSchema, vendedorResumidoSchema, } from './schemas/comercial.js';
import { enfileirarLoteSchema, enfileirarSchema, resultadoEnfileirarLoteSchema, filtroJobsSchema, jobSchema, resultadoEnfileirarSchema, resumoFilaSchema, } from './schemas/fila.js';
/**
 * Devolve o tipo LITERAL do que foi passado, nao `DefinicaoRota`.
 *
 * A diferenca importa e nao e cosmetica: com `DefinicaoRota` de retorno, `params`
 * e `corpo` sao sempre opcionais no tipo, entao `EntradaRota<K>` nao consegue
 * distinguir "esta rota tem corpo" de "esta rota nao tem" — e todo campo colapsa
 * para `undefined`. Com o tipo literal, `chamar('sites.publicar', {...})` exige o
 * corpo, e `chamar('vitrines.listar', { corpo: ... })` nao compila.
 */
function rota(d) {
    return d;
}
const paramsId = z.object({ id: z.string() });
/**
 * `:id` que e UUID de verdade.
 *
 * `paramsId` e `z.string()` cru por historia: nem todo `:id` desta API e uuid
 * (usuario do Better Auth e cuid). Onde o id E uuid, validar aqui e a diferenca
 * entre 422 com o nome do campo e um 500 do Prisma — que e erro de servidor
 * anunciado para o que na verdade e pedido malformado.
 */
const paramsIdUuid = z.object({ id: z.uuid() });
export const rotas = {
    // ---------------------------------------------------------------- saude ---
    'saude.checar': rota({
        metodo: 'GET',
        caminho: '/saude',
        resposta: z.object({
            ok: z.boolean(),
            banco: z.boolean(),
            armazenamento: z.boolean(),
            versao: z.string(),
        }),
        acesso: 'publico',
        resumo: 'Liveness + readiness. Usado pelo healthcheck da Railway e pelo smoke test.',
    }),
    // ------------------------------------------------------------- usuarios ---
    'usuarios.listar': rota({
        metodo: 'GET',
        caminho: '/v1/usuarios',
        resposta: z.array(usuarioSchema),
        acesso: 'admin',
        resumo: 'Equipe interna com cargo e gestor.',
    }),
    'usuarios.criar': rota({
        metodo: 'POST',
        caminho: '/v1/usuarios',
        corpo: criarUsuarioSchema,
        resposta: usuarioSchema,
        acesso: 'admin',
        resumo: 'Cria usuario e cargo na MESMA transacao. E a correcao estrutural do BUG-1: ' +
            'antes o usuario nascia no Auth, o cargo falhava no CHECK e sobrava orfao.',
    }),
    'usuarios.atualizar': rota({
        metodo: 'PATCH',
        caminho: '/v1/usuarios/:id',
        params: paramsId,
        corpo: atualizarUsuarioSchema,
        resposta: usuarioSchema,
        acesso: 'admin',
        resumo: 'Muda cargo, gestor ou desativa.',
    }),
    // ------------------------------------------- sessao do cliente (portal) ---
    'portal.entrar': rota({
        metodo: 'POST',
        caminho: '/v1/portal/entrar',
        corpo: loginClienteSchema,
        resposta: sessaoClienteSchema,
        acesso: 'publico',
        resumo: 'Usuario + PIN por POST. O PIN e comparado contra argon2id no servidor, com ' +
            'limite de tentativa por conta e por IP. Nunca trafega como filtro de query.',
    }),
    'portal.sessao': rota({
        metodo: 'GET',
        caminho: '/v1/portal/sessao',
        resposta: sessaoClienteSchema,
        acesso: 'cliente',
        dono: 'proprio',
        resumo: 'Quem esta logado no portal.',
    }),
    'portal.sair': rota({
        metodo: 'POST',
        caminho: '/v1/portal/sair',
        resposta: vazioSchema,
        acesso: 'cliente',
        dono: 'proprio',
        resumo: 'Encerra a sessao no SERVIDOR. Sair so apagando o token do navegador deixava ' +
            'a linha viva em `sessoes_portal` ate expirar.',
    }),
    'portal.trocarSenha': rota({
        metodo: 'POST',
        caminho: '/v1/portal/senha',
        corpo: trocarSenhaPortalSchema,
        resposta: vazioSchema,
        acesso: 'cliente',
        dono: 'proprio',
        resumo: 'O cliente troca a propria senha. Exige a atual — sessao roubada nao vira ' +
            'troca de senha. As demais sessoes do cliente caem junto.',
    }),
    // ------------------------------------------------------------- clientes ---
    'clientes.listar': rota({
        metodo: 'GET',
        caminho: '/v1/clientes',
        query: filtroClientesSchema,
        resposta: paginadoSchema(clienteSchema),
        acesso: 'operador',
        resumo: 'Paginacao por cursor. Nunca devolve HTML nem o JSONB de negocio — o artefato ' +
            'pesado nao entra em listagem, so o ponteiro.',
    }),
    'clientes.contar': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/contagem',
        query: filtroClientesSchema.omit({ cursor: true, limite: true }),
        resposta: z.object({ total: z.number().int() }),
        acesso: 'operador',
        resumo: 'Contagem separada da listagem, para o painel nao pagar COUNT em toda pagina.',
    }),
    /**
     * `cliente` e nao `operador`, com dono conferido.
     *
     * Era `operador`, e o painel do CLIENTE morria aqui: `db.loginClient` faz o
     * POST de `portal.entrar`, recebe a sessao, e a chamada seguinte para montar a
     * tela era esta — que devolvia 403 para a sessao recem-criada. A tela tratava
     * qualquer falha como credencial errada, entao o sintoma era "PIN incorreto"
     * logo depois de o PIN estar certo. No navegador do operador nao aparecia: o
     * cookie de operador vai junto e ganha do bearer do portal.
     *
     * `acesso: 'cliente'` nao afrouxa nada — o operador continua passando (e o
     * suporte), e o cliente so alcanca o proprio id, pelo `exigirDono`.
     */
    'clientes.obter': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/:id',
        params: paramsId,
        resposta: clienteSchema.extend({ dadosNegocio: dadosNegocioSchema.nullable() }),
        acesso: 'cliente',
        dono: 'cliente:params.id',
        resumo: 'Cliente com o bloco frio de dados de negocio.',
    }),
    'clientes.obterPorSlug': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/por-slug/:slug',
        params: z.object({ slug: z.string() }),
        resposta: clienteSchema.extend({ dadosNegocio: dadosNegocioSchema.nullable() }),
        acesso: 'cliente',
        dono: 'cliente:params.slug',
        resumo: 'Mesma coisa, pela chave que o painel usa nas URLs.',
    }),
    'clientes.credencial': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/:id/credencial',
        params: paramsId,
        resposta: credencialClienteSchema,
        acesso: 'operador',
        resumo: 'Se o cliente TEM acesso, qual o usuario e quando usou. Nunca a senha: o ' +
            'que fica gravado e hash argon2id, e nao ha consulta que o desfaca.',
    }),
    'clientes.definirCredencial': rota({
        metodo: 'PUT',
        caminho: '/v1/clientes/:id/credencial',
        params: paramsId,
        corpo: definirCredencialClienteSchema,
        resposta: credencialEmitidaSchema,
        acesso: 'operador',
        resumo: 'Cria ou redefine a credencial do portal. Devolve a senha em claro UMA vez, ' +
            'para o operador repassar. Substitui o `usuario`/`pin` que o painel gravava ' +
            'em texto puro dentro do JSONB `data` — onde, alem de legivel, era descartado ' +
            'pelo mapeador e nunca chegava a existir.',
    }),
    'clientes.criar': rota({
        metodo: 'POST',
        caminho: '/v1/clientes',
        corpo: criarClienteSchema,
        resposta: clienteSchema,
        acesso: 'operador',
        resumo: 'Cria cliente, dados de negocio e credencial do portal numa transacao.',
    }),
    'clientes.importar': rota({
        metodo: 'POST',
        caminho: '/v1/clientes/importar',
        corpo: importarClientesSchema,
        resposta: resultadoImportacaoSchema,
        acesso: 'operador',
        resumo: 'Lote da fabrica. ON CONFLICT DO NOTHING no slug: o antigo laco de insert um ' +
            'a um para contornar o 23505 vira uma ida so ao banco.',
    }),
    'clientes.atualizar': rota({
        metodo: 'PATCH',
        caminho: '/v1/clientes/:id',
        params: paramsId,
        corpo: atualizarClienteSchema,
        resposta: clienteSchema,
        acesso: 'operador',
        resumo: 'Atualizacao parcial.',
    }),
    'clientes.excluir': rota({
        metodo: 'POST',
        caminho: '/v1/clientes/excluir',
        corpo: z.object({ ids: z.array(z.uuid()).min(1).max(500) }),
        resposta: z.object({ excluidos: z.number().int() }),
        acesso: 'admin',
        resumo: 'Exclusao em lote. POST e nao DELETE porque leva corpo.',
    }),
    'clientes.exportar': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/exportar',
        query: filtroClientesSchema.omit({ cursor: true, limite: true }),
        resposta: z.array(z.record(z.string(), z.unknown())),
        acesso: 'operador',
        resumo: 'Dump plano para CSV/JSON. Sem HTML, com limite duro no servidor.',
    }),
    'cerebro.obter': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/:id/cerebro',
        params: paramsId,
        resposta: cerebroNegocioSchema.nullable(),
        acesso: 'cliente',
        dono: 'cliente:params.id',
        resumo: 'Dossie de 20 blocos. Tabela fria, so lida nesta tela. O painel do cliente ' +
            'le e escreve o proprio dossie (e dali que sai a marca dele).',
    }),
    'cerebro.salvar': rota({
        metodo: 'PUT',
        caminho: '/v1/clientes/:id/cerebro',
        params: paramsId,
        corpo: salvarCerebroNegocioSchema,
        resposta: cerebroNegocioSchema,
        acesso: 'cliente',
        dono: 'cliente:params.id',
        resumo: 'Upsert do dossie.',
    }),
    // ----------------------------------------------------------------- site ---
    'sites.listarPorCliente': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/:id/sites',
        params: paramsId,
        resposta: z.array(siteSchema),
        acesso: 'operador',
        resumo: 'Um cliente pode ter varios sites. Antes eram a mesma linha.',
    }),
    /**
     * As cinco rotas abaixo sao `cliente` porque o EDITOR VISUAL tambem vive no
     * painel do cliente — ele abre o site, carrega o HTML da versao no ar e
     * publica. Com elas em `operador`, a aba "Editor" do painel do cliente
     * respondia 403 em toda chamada e o canvas abria vazio.
     *
     * `site:params.id` e o que torna isso seguro: o `:id` aqui e de um SITE, nao
     * de um cliente, entao a comparacao direta do guard nunca casaria. Ele resolve
     * o dono do site antes de comparar. O operador segue passando direto.
     */
    'sites.obter': rota({
        metodo: 'GET',
        caminho: '/v1/sites/:id',
        params: paramsId,
        resposta: siteSchema.extend({ hostnames: z.array(hostnameSchema) }),
        acesso: 'cliente',
        dono: 'site:params.id',
        resumo: 'Site com os hostnames apontados para ele.',
    }),
    'sites.criar': rota({
        metodo: 'POST',
        caminho: '/v1/sites',
        corpo: criarSiteSchema,
        resposta: siteSchema,
        acesso: 'cliente',
        dono: 'destino:corpo',
        resumo: 'Cria o site com o spec inicial. Nao publica. O `clienteId` do corpo tem de ' +
            'ser o do solicitante quando quem chama e o portal.',
    }),
    'sites.obterSpec': rota({
        metodo: 'GET',
        caminho: '/v1/sites/:id/spec',
        params: paramsId,
        resposta: siteSpecSchema,
        acesso: 'cliente',
        dono: 'site:params.id',
        resumo: 'O spec da versao corrente. E o que o editor carrega.',
    }),
    'sites.publicar': rota({
        metodo: 'POST',
        caminho: '/v1/sites/:id/publicar',
        params: paramsId,
        corpo: publicarSiteSchema,
        resposta: resultadoPublicacaoSchema,
        acesso: 'cliente',
        dono: 'site:params.id',
        resumo: 'Renderiza o spec, sobe o HTML para o R2, grava a versao e vira o ponteiro. ' +
            'Se o hash bater com a versao corrente, nao escreve nada e devolve escreveuNoR2=false.',
    }),
    'sites.listarVersoes': rota({
        metodo: 'GET',
        caminho: '/v1/sites/:id/versoes',
        params: paramsId,
        resposta: z.array(versaoSiteSchema.omit({ spec: true })),
        acesso: 'cliente',
        dono: 'site:params.id',
        resumo: 'Historico. Sem o spec, para a listagem continuar barata.',
    }),
    'sites.reverter': rota({
        metodo: 'POST',
        caminho: '/v1/sites/:id/reverter',
        params: paramsId,
        corpo: z.object({ versaoId: z.uuid() }),
        resposta: resultadoPublicacaoSchema,
        acesso: 'operador',
        resumo: 'Voltar uma versao e trocar o ponteiro, nao regerar. O artefato antigo ' +
            'continua no R2 exatamente como foi publicado.',
    }),
    /**
     * O HTML de uma versao ja publicada, lido do R2 PELA API.
     *
     * O painel lia esse artefato direto do CDN, montando
     * `VITE_CDN_BASE_SITES + chaveR2`. Isso exigia que o bucket de sites fosse
     * publico e que a variavel apontasse para o dominio ligado a ELE — e um
     * dominio de R2 serve um bucket so. Com `CDN_BASE_SITES` e `CDN_BASE_ASSETS`
     * no mesmo endereco, a leitura caia no bucket de imagens e voltava
     * `404 Object not found` com o objeto intacto no bucket certo.
     *
     * Aqui nao ha essa armadilha: quem le e a API, que ja tem credencial do R2 e
     * ja sabe qual balde e qual. E o bucket de sites nao precisa mais ser publico
     * para o painel funcionar.
     *
     * Nao e caminho de escala: serve o operador olhando UM site. Quem serve os
     * visitantes continua sendo o Worker da borda, direto do R2, sem passar aqui.
     */
    'sites.obterHtmlVersao': rota({
        metodo: 'GET',
        caminho: '/v1/sites/:id/versoes/:versaoId/html',
        params: z.object({ id: z.uuid(), versaoId: z.uuid() }),
        resposta: z.object({
            html: z.string(),
            bytes: z.number().int().nonnegative(),
            chaveR2: z.string(),
            hashConteudo: z.string(),
        }),
        acesso: 'cliente',
        dono: 'site:params.id',
        resumo: 'HTML de uma versao publicada, lido do R2 pela API. Nao exige bucket publico.',
    }),
    'sites.previa': rota({
        metodo: 'POST',
        caminho: '/v1/sites/previa',
        corpo: z.object({ spec: siteSpecSchema }),
        resposta: z.object({ html: z.string(), bytes: z.number().int() }),
        acesso: 'operador',
        resumo: 'Renderiza sem gravar. O editor usa para o preview.',
    }),
    // ------------------------------------------------------------ hostnames ---
    'hostnames.listar': rota({
        metodo: 'GET',
        caminho: '/v1/sites/:id/hostnames',
        params: paramsId,
        resposta: z.array(hostnameSchema),
        acesso: 'operador',
        resumo: 'Dominios apontados para um site.',
    }),
    'hostnames.adicionar': rota({
        metodo: 'POST',
        caminho: '/v1/sites/:id/hostnames',
        params: paramsId,
        corpo: criarHostnameSchema,
        resposta: hostnameSchema,
        acesso: 'operador',
        resumo: 'Unico parcial no banco impede dois sites com o mesmo dominio (BUG-4). ' +
            'A validacao no front nunca foi garantia.',
    }),
    'hostnames.remover': rota({
        metodo: 'DELETE',
        caminho: '/v1/hostnames/:hostname',
        params: z.object({ hostname: z.string() }),
        resposta: vazioSchema,
        acesso: 'operador',
        resumo: 'Remove o dominio e agenda a remocao do custom hostname na Cloudflare.',
    }),
    'hostnames.resolver': rota({
        metodo: 'GET',
        caminho: '/v1/resolver/:hostname',
        params: z.object({ hostname: z.string() }),
        resposta: resolucaoHostnameSchema,
        acesso: 'publico',
        resumo: 'Plano de dados: hostname -> chave do artefato no R2. E o que o Worker da ' +
            'borda consulta no MISS de cache, e o que popula o KV.',
    }),
    // --------------------------------------------------------------- assets ---
    'assets.assinarUpload': rota({
        metodo: 'POST',
        caminho: '/v1/assets/assinar',
        corpo: z.object({
            nomeArquivo: z.string().min(1),
            mime: z.string().min(1),
            bytes: z.number().int().positive().max(20 * 1024 * 1024),
            siteId: z.uuid().optional(),
            clienteId: z.uuid().optional(),
        }),
        resposta: z.object({
            url: z.string(),
            chaveR2: z.string(),
            campos: z.record(z.string(), z.string()).default({}),
            expiraEm: z.number().int(),
        }),
        acesso: 'cliente',
        dono: 'destino:corpo',
        resumo: 'URL pre-assinada: o navegador manda os bytes DIRETO para o R2, sem passar ' +
            'pela API. E o que impede a imagem de virar egress da Railway. Do portal, ' +
            'exige `clienteId`/`siteId` do proprio solicitante — upload de cliente nao ' +
            'pode ser sem destino, senao o dono nao tem como ser conferido.',
    }),
    'assets.registrar': rota({
        metodo: 'POST',
        caminho: '/v1/assets',
        corpo: z.object({
            chaveR2: z.string(),
            mime: z.string(),
            bytes: z.number().int(),
            largura: z.number().int().optional(),
            altura: z.number().int().optional(),
            siteId: z.uuid().optional(),
            clienteId: z.uuid().optional(),
            /**
             * Para que serve esta imagem.
             *
             * `logo` e a foto de perfil (do WhatsApp, no export da captacao): ela vira
             * o marcador `[LOGO]` do prompt e vai para o cabecalho do site. `galeria`
             * e foto do estabelecimento. Sem esta distincao a geracao trataria a foto
             * de perfil como mais uma da galeria — e o cabecalho sairia sem marca, com
             * a logo perdida no meio das fotos de servico.
             */
            papel: papelAssetSchema.optional(),
        }),
        resposta: z.object({ id: z.uuid(), url: z.string() }),
        acesso: 'cliente',
        dono: 'destino:corpo',
        resumo: 'Confirma o upload e registra o objeto. Chamado depois do PUT no R2.',
    }),
    // ----------------------------------------------- importacao em massa ---
    'importacoes.assinarEnvio': rota({
        metodo: 'POST',
        caminho: '/v1/importacoes/assinar-envio',
        corpo: z.object({
            /** Rotula o lote. Vira o primeiro nivel da chave e aparece no log. */
            lote: z.string().min(1).max(120),
            /** A pasta do site dentro do ZIP. */
            pasta: z.string().min(1).max(200),
            /** `index.html` ou `data.json`. Lista fechada: nao e upload livre. */
            arquivo: z.enum(['index.html', 'data.json']),
            bytes: z.number().int().positive().max(50 * 1024 * 1024),
        }),
        resposta: z.object({
            url: z.string(),
            chaveR2: z.string(),
            expiraEm: z.number().int(),
        }),
        acesso: 'operador',
        resumo: 'URL pre-assinada para o navegador subir os arquivos do export DIRETO para o ' +
            'balde PRIVADO, sem passar pela API. Existe separada de `assets.assinarUpload` ' +
            'porque aquela so aceita MIME de imagem e grava no balde publico do CDN: ' +
            'assinar `text/html` la seria XSS armazenado num dominio da marca. Aqui o ' +
            'destino nunca e servido a anonimo e o nome do arquivo vem de lista fechada.',
    }),
    'assets.assinarLeitura': rota({
        metodo: 'POST',
        caminho: '/v1/assets/assinar-leitura',
        corpo: z.object({
            /** Chave no balde PRIVADO, obrigatoriamente sob `importacoes/`. */
            chaveR2: z.string().min(1).max(1024),
        }),
        resposta: z.object({ url: z.string(), expiraEm: z.number().int() }),
        acesso: 'servico',
        resumo: 'URL pre-assinada de LEITURA no balde privado, para o worker baixar do R2 sem ' +
            'os bytes atravessarem a API. Cada site traz um HTML de ~820 KB; passar isso ' +
            'pelo processo Node seria ~4 GB de egress em cinco mil sites. Restrita a ' +
            '`servico` e ao prefixo `importacoes/`.',
    }),
    'assets.listar': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/:id/assets',
        params: paramsId,
        query: paginacaoSchema,
        resposta: paginadoSchema(assetSchema),
        acesso: 'cliente',
        // Debaixo de `/v1/clientes/:id/` de proposito: reaproveita a estrategia de
        // dono que ja existe, em vez de inventar uma sexta. O cliente ve as SUAS
        // imagens; trocar o `:id` na URL cai no mesmo 403 do resto do portal.
        dono: 'cliente:params.id',
        resumo: 'As imagens que este cliente ja subiu, da mais nova para a mais antiga. ' +
            'Sem ela nao existe biblioteca: cada upload preenchia um campo e sumia, ' +
            'entao usar a mesma foto em dois lugares exigia subir o arquivo duas vezes.',
    }),
    'assets.remover': rota({
        metodo: 'DELETE',
        caminho: '/v1/clientes/:id/assets/:assetId',
        params: z.object({ id: z.uuid(), assetId: z.uuid() }),
        resposta: z.object({ removido: z.boolean() }),
        acesso: 'cliente',
        dono: 'cliente:params.id',
        resumo: 'Tira a imagem da biblioteca e do R2. Nao mexe nos specs que a referenciam: ' +
            'quem apaga uma imagem em uso ve o buraco na propria previa, e isso e mais ' +
            'honesto que uma varredura silenciosa em 100 mil specs.',
    }),
    'assets.enviarBase64': rota({
        metodo: 'POST',
        caminho: '/v1/assets/base64',
        corpo: uploadFotoLeadSchema.extend({ mime: z.string().default('image/jpeg') }),
        resposta: resultadoUploadSchema,
        acesso: 'operador',
        resumo: 'Ponte para o codigo que ainda produz base64 (foto de lead, importacao). ' +
            'Converte e grava no R2; o base64 nao e persistido em lugar nenhum.',
    }),
    // ------------------------------------------------------------- captacao ---
    'campanhas.listar': rota({
        metodo: 'GET',
        caminho: '/v1/campanhas',
        resposta: z.array(campanhaSchema),
        acesso: 'operador',
        resumo: 'Campanhas com os passos.',
    }),
    'campanhas.salvar': rota({
        metodo: 'PUT',
        caminho: '/v1/campanhas/:id',
        params: paramsId,
        corpo: salvarCampanhaSchema,
        resposta: campanhaSchema,
        acesso: 'operador',
        resumo: 'Upsert de campanha e passos numa transacao.',
    }),
    'campanhas.excluir': rota({
        metodo: 'DELETE',
        caminho: '/v1/campanhas/:id',
        params: paramsId,
        resposta: vazioSchema,
        acesso: 'operador',
        resumo: 'Cascata nos passos.',
    }),
    'campanhas.atualizarPasso': rota({
        metodo: 'PATCH',
        caminho: '/v1/campanhas/:id/passos/:passoId',
        params: z.object({ id: z.string(), passoId: z.string() }),
        corpo: z.object({
            status: z.enum(['pendente', 'rodando', 'concluido', 'falhou', 'pulado']).optional(),
            leadsEncontrados: z.number().int().optional(),
            erro: z.string().nullable().optional(),
            concluidoEm: z.string().nullable().optional(),
        }),
        resposta: vazioSchema,
        acesso: 'operador',
        resumo: 'Progresso de um passo. `erro` existe para a falha nao sumir (BUG-6).',
    }),
    'leads.listar': rota({
        metodo: 'GET',
        caminho: '/v1/leads',
        query: filtroLeadsSchema,
        resposta: paginadoSchema(leadSchema),
        acesso: 'operador',
        resumo: 'Sempre com limite. Nunca a tabela inteira.',
    }),
    'leads.salvar': rota({
        metodo: 'POST',
        caminho: '/v1/leads',
        corpo: salvarLeadsSchema,
        resposta: z.object({ salvos: z.number().int(), atualizados: z.number().int() }),
        acesso: 'operador',
        resumo: 'Upsert por placeId. Reprocessar a mesma regiao nao duplica lead.',
    }),
    'leads.excluir': rota({
        metodo: 'POST',
        caminho: '/v1/leads/excluir',
        corpo: excluirLeadsSchema,
        resposta: z.object({ excluidos: z.number().int() }),
        acesso: 'operador',
        resumo: 'Exclusao em lote por placeId.',
    }),
    'configsJson.listar': rota({
        metodo: 'GET',
        caminho: '/v1/configs-json',
        resposta: z.array(configJsonSchema),
        acesso: 'operador',
        resumo: 'Central de JSON do nyroh-leads.',
    }),
    'configsJson.salvar': rota({
        metodo: 'POST',
        caminho: '/v1/configs-json',
        corpo: salvarConfigJsonSchema,
        resposta: configJsonSchema,
        acesso: 'operador',
        resumo: 'Upsert.',
    }),
    'configsJson.excluir': rota({
        metodo: 'DELETE',
        caminho: '/v1/configs-json/:id',
        params: paramsId,
        resposta: vazioSchema,
        acesso: 'operador',
        resumo: 'Remove uma configuracao.',
    }),
    // ----------------------------------------------------------- financeiro ---
    'pagamentos.listar': rota({
        metodo: 'GET',
        caminho: '/v1/pagamentos',
        query: z.object({ clienteId: z.uuid().optional() }),
        resposta: z.array(pagamentoSchema),
        acesso: 'operador',
        resumo: 'Cobranca da Nyroh ao dono do site.',
    }),
    'pagamentos.criar': rota({
        metodo: 'POST',
        caminho: '/v1/pagamentos',
        corpo: criarPagamentoSchema,
        resposta: pagamentoSchema,
        acesso: 'operador',
        resumo: 'Registra a cobranca.',
    }),
    'pagamentos.atualizarStatus': rota({
        metodo: 'PATCH',
        caminho: '/v1/pagamentos/:id',
        params: paramsId,
        corpo: atualizarPagamentoSchema,
        resposta: pagamentoSchema,
        acesso: 'operador',
        resumo: 'Confirma, reprova ou anexa comprovante.',
    }),
    'faturas.listar': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/:id/faturas',
        params: paramsId,
        query: filtroFaturasSchema,
        resposta: paginadoSchema(faturaSchema),
        acesso: 'cliente',
        resumo: 'Cobranca do dono do site ao consumidor final dele.',
    }),
    'faturas.obter': rota({
        metodo: 'GET',
        caminho: '/v1/faturas/:id',
        params: paramsId,
        resposta: faturaSchema,
        acesso: 'publico',
        resumo: 'Pagina publica de pagamento. So o id, sem listar nada em volta.',
    }),
    'faturas.criar': rota({
        metodo: 'POST',
        caminho: '/v1/clientes/:id/faturas',
        params: paramsId,
        corpo: criarFaturaSchema,
        resposta: faturaSchema,
        acesso: 'cliente',
        resumo: 'Numero sequencial gerado no banco, nao no navegador.',
    }),
    'faturas.pagar': rota({
        metodo: 'POST',
        caminho: '/v1/faturas/:id/pagar',
        params: paramsId,
        corpo: pagarFaturaSchema,
        resposta: faturaSchema,
        acesso: 'publico',
        resumo: 'Marca como paga e registra o evento na linha do tempo do consumidor.',
    }),
    'consumidores.listar': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/:id/consumidores',
        params: paramsId,
        resposta: z.array(perfilConsumidorSchema),
        acesso: 'cliente',
        resumo: 'Dossie 360. Antes vivia em localStorage com quatro pessoas fixas de exemplo; ' +
            'sumia ao trocar de navegador e nao existia para mais ninguem.',
    }),
    'consumidores.salvar': rota({
        metodo: 'PUT',
        caminho: '/v1/clientes/:id/consumidores',
        params: paramsId,
        corpo: salvarPerfilConsumidorSchema,
        resposta: perfilConsumidorSchema,
        acesso: 'cliente',
        resumo: 'Upsert do perfil.',
    }),
    'configPagamento.obter': rota({
        metodo: 'GET',
        caminho: '/v1/clientes/:id/config-pagamento',
        params: paramsId,
        resposta: configPagamentoClienteSchema,
        acesso: 'cliente',
        resumo: 'Chaves Pix e gateway do cliente. O token do gateway nunca volta.',
    }),
    'configPagamento.salvar': rota({
        metodo: 'PUT',
        caminho: '/v1/clientes/:id/config-pagamento',
        params: paramsId,
        corpo: salvarConfigPagamentoSchema,
        resposta: configPagamentoClienteSchema,
        acesso: 'cliente',
        resumo: 'Grava o token cifrado; devolve so o booleano de configurado.',
    }),
    'configGlobal.obter': rota({
        metodo: 'GET',
        caminho: '/v1/config-global',
        resposta: configGlobalSchema,
        acesso: 'operador',
        resumo: 'Preco padrao, Pix da Nyroh, dominio base dos sites.',
    }),
    'configGlobal.salvar': rota({
        metodo: 'PUT',
        caminho: '/v1/config-global',
        corpo: salvarConfigGlobalSchema,
        resposta: configGlobalSchema,
        acesso: 'admin',
        resumo: 'So admin.',
    }),
    // -------------------------------------------------------------- vitrine ---
    'vitrines.listar': rota({
        metodo: 'GET',
        caminho: '/v1/vitrines',
        resposta: z.array(vitrineSchema),
        acesso: 'operador',
        resumo: 'Vitrines de portfolio.',
    }),
    'vitrines.obterPorSlug': rota({
        metodo: 'GET',
        caminho: '/v1/vitrines/por-slug/:slug',
        params: z.object({ slug: z.string() }),
        resposta: vitrineSchema,
        acesso: 'publico',
        resumo: 'Pagina publica da vitrine.',
    }),
    'vitrines.salvar': rota({
        metodo: 'PUT',
        caminho: '/v1/vitrines',
        corpo: salvarVitrineSchema,
        resposta: vitrineSchema,
        acesso: 'operador',
        resumo: 'Upsert com os itens.',
    }),
    'vitrines.excluir': rota({
        metodo: 'DELETE',
        caminho: '/v1/vitrines/:id',
        params: paramsId,
        resposta: vazioSchema,
        acesso: 'operador',
        resumo: 'Remove a vitrine e os itens.',
    }),
    // ----------------------------------------------------------------- fila ---
    'fila.enfileirar': rota({
        metodo: 'POST',
        caminho: '/v1/fila/jobs',
        corpo: enfileirarSchema,
        resposta: resultadoEnfileirarSchema,
        acesso: 'operador',
        resumo: 'Enfileira trabalho. Chave de idempotencia impede duplicata.',
    }),
    'fila.enfileirarLote': rota({
        metodo: 'POST',
        caminho: '/v1/fila/jobs/lote',
        corpo: enfileirarLoteSchema,
        resposta: resultadoEnfileirarLoteSchema,
        acesso: 'operador',
        resumo: 'Ate 500 jobs numa ida. A importacao em massa enfileira um job por SITE; ' +
            'com cinco mil sites, um POST por job seria cinco mil idas de rede e a tela ' +
            'travada por minutos.',
    }),
    'fila.listar': rota({
        metodo: 'GET',
        caminho: '/v1/fila/jobs',
        query: filtroJobsSchema,
        resposta: paginadoSchema(jobSchema),
        acesso: 'operador',
        resumo: 'Inspecao da fila.',
    }),
    'fila.resumo': rota({
        metodo: 'GET',
        caminho: '/v1/fila/resumo',
        resposta: resumoFilaSchema,
        acesso: 'operador',
        resumo: 'Contagem por status e por tipo, mais o tamanho do dead letter.',
    }),
    'fila.reenfileirarDeadLetter': rota({
        metodo: 'POST',
        caminho: '/v1/fila/dead-letter/:id/reenfileirar',
        params: paramsId,
        resposta: resultadoEnfileirarSchema,
        acesso: 'operador',
        resumo: 'Devolve um job morto para a fila depois de corrigida a causa.',
    }),
    // ----------------------------------------------------------- comercial ---
    //
    // O painel do vendedor. Nenhuma rota deste bloco aceita id de vendedor: o
    // servidor deriva o dono da sessao. Ler dado de OUTRO vendedor exige as rotas
    // `comercial.lideranca.*`, que sao `acesso: 'lideranca'` e existem separadas
    // exatamente para que a diferenca fique no contrato, e nao num `if` dentro de
    // um handler.
    //
    // O que isso substitui: dez endpoints que aceitavam `?sellerId=` COM
    // PRECEDENCIA sobre o usuario logado. Trocar o id na URL lia a carteira, as
    // vendas e a comissao de qualquer outra pessoa. Nao ha correcao possivel
    // mantendo o parametro — a correcao e o parametro deixar de existir.
    'comercial.eu': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/eu',
        resposta: identidadeComercialSchema,
        acesso: 'operador',
        resumo: 'Quem esta logado, com `ehLideranca` calculado NO SERVIDOR a partir do ' +
            'cargo. A tela nao decide o proprio nivel de acesso: a versao anterior lia ' +
            '`role` de um JSON no localStorage, e trocar "VENDEDOR" por "LIDER" no ' +
            'DevTools abria o painel de gestao.',
    }),
    'comercial.resumo': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/resumo',
        resposta: resumoVendedorSchema,
        acesso: 'operador',
        resumo: 'Os numeros do proprio vendedor, por count/sum com janela de data real. ' +
            'Devolve ZERO quando e zero — nao ha piso inventado.',
    }),
    'comercial.leads.listar': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/leads',
        query: filtroLeadsComerciaisSchema,
        resposta: paginadoSchema(leadComercialSchema),
        acesso: 'operador',
        resumo: 'A carteira do solicitante, paginada por cursor. Versao estreita do lead: ' +
            'timeline, mensagens e ligacoes so no detalhe.',
    }),
    'comercial.leads.obter': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/leads/:id',
        // `paramsIdUuid`, nao `paramsId`. O `:id` de um lead e uuid, e com
        // `z.string()` cru um id malformado atravessava a validacao e chegava ao
        // Prisma, que respondia 500 — erro de servidor para o que e erro de pedido.
        params: paramsIdUuid,
        resposta: leadComercialDetalheSchema,
        acesso: 'operador',
        resumo: 'O lead com historico. Lead de outro vendedor responde 404, nao 403: o ' +
            'WHERE do servico ja carrega o dono, entao a rota nem chega a saber que o ' +
            'registro existe. 403 confirmaria a existencia do id para quem sondasse.',
    }),
    'comercial.leads.mudarStatus': rota({
        metodo: 'POST',
        caminho: '/v1/comercial/leads/:id/status',
        params: paramsIdUuid,
        corpo: mudarStatusLeadSchema,
        resposta: leadComercialSchema,
        acesso: 'operador',
        resumo: 'Move o lead de etapa e grava o evento na timeline, na mesma transacao.',
    }),
    'comercial.leads.registrarLigacao': rota({
        metodo: 'POST',
        caminho: '/v1/comercial/leads/:id/ligacoes',
        params: paramsIdUuid,
        corpo: registrarLigacaoSchema,
        resposta: leadComercialDetalheSchema,
        acesso: 'operador',
        resumo: 'Uma linha por ligacao, com timestamp. O numero da tentativa e contado no ' +
            'servidor a partir do que ja esta gravado, e o status do lead e derivado do ' +
            'resultado — nao vem do navegador.',
    }),
    'comercial.leads.registrarMensagem': rota({
        metodo: 'POST',
        caminho: '/v1/comercial/leads/:id/mensagens',
        params: paramsIdUuid,
        corpo: registrarMensagemSchema,
        resposta: leadComercialDetalheSchema,
        acesso: 'operador',
        resumo: 'REGISTRA a mensagem; nao envia. O envio real de WhatsApp e job na fila com ' +
            'cota, chamando a Evolution a partir da API — chave de terceiro nunca no ' +
            'navegador. `ehLinkDeSite` e o que alimenta a aba SITES ENVIADOS.',
    }),
    'comercial.vendas.listar': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/vendas',
        query: filtroVendasSchema,
        resposta: paginadoSchema(vendaComercialSchema),
        acesso: 'operador',
        resumo: 'As vendas do proprio solicitante, paginadas por cursor.',
    }),
    'comercial.vendas.fechar': rota({
        metodo: 'POST',
        caminho: '/v1/comercial/vendas',
        corpo: fecharVendaSchema,
        resposta: vendaComercialSchema,
        acesso: 'operador',
        resumo: 'Fecha a venda e move o lead para `fechado` na mesma transacao. O ' +
            'percentual de comissao vem da configuracao do servidor e fica GRAVADO na ' +
            'venda; o valor e calculado la. O corpo nao traz comissao — ela era ' +
            'calculada em quatro lugares do navegador, com 30 literal, por quem recebe ' +
            'o pagamento. O `leadId` tem de ser da carteira do solicitante, ou 404.',
    }),
    'comercial.lideranca.resumo': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/lideranca/resumo',
        resposta: resumoLiderancaSchema,
        acesso: 'lideranca',
        resumo: 'Os numeros da operacao inteira. `metricasIndisponiveis` nomeia o que NAO ' +
            'da para calcular hoje (ramp-up, churn, ROI) em vez de devolver um numero ' +
            'inventado — a versao anterior devolvia os tres como literal fixo.',
    }),
    'comercial.lideranca.vendedores': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/lideranca/vendedores',
        query: paginacaoSchema,
        resposta: paginadoSchema(vendedorResumidoSchema),
        acesso: 'lideranca',
        resumo: 'A equipe com os agregados de cada um, paginada por cursor como toda ' +
            'listagem desta API. A versao anterior devolvia `{ itens }` cru com teto ' +
            'de 500 no servico e NENHUM aviso ao chegar nele — a partir do 501o ' +
            'vendedor a equipe simplesmente encolhia na tela, sem erro.',
    }),
    'comercial.lideranca.leadsDoVendedor': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/lideranca/vendedores/:id/leads',
        params: paramsVendedorSchema,
        query: filtroLeadsComerciaisSchema,
        resposta: paginadoSchema(leadComercialSchema),
        acesso: 'lideranca',
        resumo: 'O modo auditoria: a carteira de OUTRA pessoa. E rota separada de proposito ' +
            '— e o que substitui o `?sellerId=` que qualquer vendedor podia mandar.',
    }),
    'comercial.lideranca.vendasDoVendedor': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/lideranca/vendedores/:id/vendas',
        params: paramsVendedorSchema,
        query: filtroVendasSchema,
        resposta: paginadoSchema(vendaComercialSchema),
        acesso: 'lideranca',
        resumo: 'As vendas de outra pessoa, pelo mesmo motivo da rota acima.',
    }),
    'comercial.lideranca.distribuir': rota({
        metodo: 'POST',
        caminho: '/v1/comercial/lideranca/distribuir',
        corpo: distribuirLeadsSchema,
        resposta: resultadoDistribuicaoSchema,
        acesso: 'lideranca',
        resumo: 'Entrega N leads sem dono a um vendedor, do mais antigo para o mais novo ' +
            '(ou priorizando os marcados pelo disparo). Atribuicao e privilegio de ' +
            'gestao: um vendedor nao escolhe a propria carteira.',
    }),
    'comercial.lideranca.distribuirEmLote': rota({
        metodo: 'POST',
        caminho: '/v1/comercial/lideranca/distribuir-lote',
        corpo: distribuirEmLoteSchema,
        resposta: resultadoDistribuicaoLoteSchema,
        acesso: 'lideranca',
        resumo: 'Entrega N leads a CADA um de varios vendedores, numa transacao so. E a ' +
            'rota de "abastecer a equipe" — a alternativa era um laco de `distribuir` ' +
            'no navegador, que sao N transacoes independentes disputando a MESMA fila: ' +
            'a terceira falha com as duas primeiras ja gravadas, e o pedido de quem ' +
            'chegou depois volta menor sem explicacao. Todos os destinos sao validados ' +
            'ANTES da primeira escrita e a resposta traz `pedidos` ao lado de ' +
            '`atribuidos` em cada linha, porque "recebeu 4" sozinho nao diz se foi ' +
            'sucesso ou falta de lead.',
    }),
    'comercial.lideranca.criarVendedor': rota({
        metodo: 'POST',
        caminho: '/v1/comercial/lideranca/vendedores',
        corpo: criarVendedorSchema,
        resposta: vendedorResumidoSchema,
        acesso: 'admin',
        resumo: 'Cadastra vendedor ou lider sem sair do painel comercial. O hash e o mesmo ' +
            'argon2id de `usuarios.criar` (a implementacao e reaproveitada, nao ' +
            'copiada) e a senha minima e a mesma — o que muda e o `cargo`, restrito aos ' +
            'cargos de campo, para um formulario de vendedor nao virar caminho de criar ' +
            'CEO por engano. `acesso: admin` porque criar operador e criar credencial. ' +
            'A versao anterior desta tela tinha um cadastro que trazia senha padrao ' +
            'curta escrita no proprio componente e a mandava em texto para um endpoint ' +
            'proprio; ele foi removido e esta e a volta dele feita direito.',
    }),
    'comercial.lideranca.alterarVendedor': rota({
        metodo: 'PATCH',
        caminho: '/v1/comercial/lideranca/vendedores/:id',
        params: paramsVendedorSchema,
        corpo: alterarVendedorSchema,
        resposta: resultadoAlteracaoVendedorSchema,
        acesso: 'admin',
        resumo: 'Ativa, desativa, renomeia ou troca o cargo de campo de um vendedor. ' +
            'DESATIVAR (ou mover para fora dos cargos de campo) devolve a carteira ' +
            'inteira para a fila comum na MESMA transacao, e a resposta diz quantos ' +
            'leads foram devolvidos: lead preso numa conta desativada nao esta "sem ' +
            'dono", entao nao aparece em `leadsSemDono`, nao entra em distribuicao ' +
            'nenhuma e o dono nao consegue abrir o painel — ele some da operacao sem ' +
            'sumir do banco.',
    }),
    'comercial.lideranca.configuracao': rota({
        metodo: 'GET',
        caminho: '/v1/comercial/lideranca/configuracao',
        resposta: configComercialSchema,
        acesso: 'lideranca',
        resumo: 'O percentual de comissao em vigor. A lideranca precisa poder CONFERIR a ' +
            'taxa que o servidor aplica — ela ficou uma versao inteira so no banco, ' +
            'sem rota que a lesse.',
    }),
    'comercial.definirComissao': rota({
        metodo: 'PUT',
        caminho: '/v1/comercial/configuracao/comissao',
        corpo: definirComissaoSchema,
        resposta: configComercialSchema,
        acesso: 'admin',
        resumo: 'Muda o percentual para as vendas FUTURAS. Venda ja fechada guarda a taxa ' +
            'do dia em que foi fechada (`VendaComercial.percentualComissao`) e nao e ' +
            'reescrita — mudar a politica nao pode alterar o que ja foi combinado, e em ' +
            'muitos casos ja foi pago. `acesso: admin` porque isto decide pagamento.',
    }),
    'comercial.lideranca.promoverDaCaptacao': rota({
        metodo: 'POST',
        caminho: '/v1/comercial/lideranca/promover-da-captacao',
        corpo: promoverLeadsCaptacaoSchema,
        resposta: resultadoPromocaoSchema,
        acesso: 'lideranca',
        resumo: 'A ponte captacao -> comercial. Promove leads da varredura do Google Maps ' +
            'para leads trabalhaveis, gravando o `placeId` de origem. As duas tabelas ' +
            'continuam separadas: a varredura tem de poder reprocessar a mesma regiao ' +
            'sem sobrescrever o trabalho de ninguem.',
    }),
    // ------------------------------------------------------------------ ia ---
    'ia.gerar': rota({
        metodo: 'POST',
        caminho: '/v1/ia/gerar',
        corpo: gerarComIaSchema,
        resposta: respostaIaSchema,
        acesso: 'operador',
        resumo: 'O navegador chama isto; a API chama o Gemini. E o que tira a chave do ' +
            'bundle (B-07) e o que permite medir custo por cliente e aplicar cota. ' +
            'A resposta traz o consumo em TOKENS da propria chamada, ja gravado em ' +
            '`uso_ia`: antes o custo era um valor fixo de R$ 0,18 lido do localStorage ' +
            'do navegador, o que nao e medicao de coisa nenhuma.',
    }),
    'ia.gerarParaCliente': rota({
        metodo: 'POST',
        caminho: '/v1/clientes/:id/ia/gerar',
        params: paramsIdUuid,
        corpo: gerarComIaSchema.omit({ clienteId: true }),
        resposta: respostaIaSchema,
        acesso: 'cliente',
        dono: 'cliente:params.id',
        resumo: 'A mesma geracao de `ia.gerar`, mas alcancavel pelo PORTAL do cliente — o ' +
            'painel do cliente tem geradores de anuncio e de conteudo social. Antes ' +
            'eles chamavam `/api/generate-ads` no host Express do painel, uma rota SEM ' +
            'autenticacao nenhuma que gastava a chave paga: qualquer pessoa que ' +
            'alcancasse o host queimava a cota. Aqui o `:id` da URL e conferido contra ' +
            'a sessao de portal por `exigirDono`, entao o cliente so gera para si — e ' +
            'o `clienteId` sai do caminho conferido, nunca do corpo, para nao existir ' +
            'campo que se possa falsificar. O gasto vai para a linha dele em `uso_ia`.',
    }),
    'ia.gerarSite': rota({
        metodo: 'POST',
        caminho: '/v1/ia/gerar-site',
        corpo: gerarSiteComIaSchema,
        resposta: respostaGerarSiteSchema,
        acesso: 'operador',
        resumo: 'A pagina de UM cliente que ja esta no banco, com o prompt montado no ' +
            'SERVIDOR a partir do cadastro e do dossie. E o que a esteira do worker ' +
            'chama: ate agora o job `gerar_site` nunca chamou a IA, entao a fabrica ' +
            'sempre entregava o template. `html: null` nao e erro — e "nao deu, use o ' +
            'template", e `motivo` diz qual dos casos foi.',
    }),
    'ia.config': rota({
        metodo: 'GET',
        caminho: '/v1/ia/config',
        resposta: configIaSchema,
        acesso: 'operador',
        resumo: 'Qual chave esta em uso (banco ou ambiente), qual modelo, qual modo da ' +
            'esteira e qual a cota. NUNCA devolve a chave — so os quatro ultimos ' +
            'caracteres, que respondem "e a que eu colei?" sem entregar nada.',
    }),
    'ia.salvarConfig': rota({
        metodo: 'PUT',
        caminho: '/v1/ia/config',
        corpo: salvarConfigIaSchema,
        resposta: configIaSchema,
        acesso: 'admin',
        resumo: 'Grava a chave (cifrada com AES-256-GCM), o modelo e o modo da esteira. ' +
            '`admin` porque a chave e credencial paga e o modo decide gasto. Chave ' +
            'vazia APAGA a do banco e devolve o sistema a `GEMINI_API_KEY` do ' +
            'ambiente — a queda continua existindo, so deixou de ser a unica opcao.',
    }),
    'ia.modelos': rota({
        metodo: 'GET',
        caminho: '/v1/ia/modelos',
        resposta: listaModelosSchema,
        acesso: 'operador',
        resumo: 'Os modelos que a CHAVE alcanca, perguntados ao vivo ao provedor, cruzados ' +
            'com o preco cadastrado de cada um. Lista chumbada em codigo ofereceria na ' +
            'tela modelo que a conta nao tem, e o erro so apareceria na primeira ' +
            'geracao do lote.',
    }),
    'ia.salvarPreco': rota({
        metodo: 'PUT',
        caminho: '/v1/ia/precos/:modelo',
        params: z.object({ modelo: z.string().min(1).max(120) }),
        corpo: salvarPrecoModeloSchema,
        resposta: vazioSchema,
        acesso: 'admin',
        resumo: 'O preco de um modelo, em dolar por milhao de tokens, com a data em que ' +
            'foi conferido. Preco e DADO, nao codigo: a tabela do provedor muda sozinha, ' +
            'e preco chumbado vira relatorio errado que ninguem percebe.',
    }),
    'ia.precosDoProvedor': rota({
        metodo: 'GET',
        caminho: '/v1/ia/precos/provedor',
        resposta: catalogoPrecosSchema,
        acesso: 'admin',
        resumo: 'Le o preco publicado pelo Google na Cloud Billing Catalog API (servico ' +
            '`generativelanguage.googleapis.com`) e casa cada SKU com os modelos que a ' +
            'chave alcanca. NAO grava nada: devolve proposta, com a descricao do SKU e o ' +
            'preco unitario cru ao lado do valor convertido. O casamento SKU -> modelo e ' +
            'por texto e tem variante por tier e regiao — importar em silencio trocaria ' +
            '"preco digitado errado" por "preco do SKU errado", que parece automatico e ' +
            'ninguem confere.',
    }),
    'ia.aplicarPrecosDoProvedor': rota({
        metodo: 'POST',
        caminho: '/v1/ia/precos/provedor/aplicar',
        corpo: aplicarPrecosSchema,
        resposta: resultadoAplicarPrecosSchema,
        acesso: 'admin',
        resumo: 'Grava em `precos_modelo_ia` os modelos MARCADOS na tela, com ' +
            '`fonte: cloudbilling` e `conferido_em` de hoje. Consulta o catalogo de novo ' +
            'em vez de confiar no corpo: preco enviado pelo navegador seria preco ' +
            'digitavel por quem chama a rota, e o ponto desta rota e ele nao ser.',
    }),
    'ia.diagnosticar': rota({
        metodo: 'POST',
        caminho: '/v1/ia/diagnostico',
        corpo: diagnosticarIaSchema,
        resposta: diagnosticoIaSchema,
        acesso: 'operador',
        resumo: 'Chamada real ao provedor para dizer se a chave funciona. Aceita uma chave ' +
            'ainda NAO salva, que e a ordem certa: colar, testar, so entao gravar. ' +
            'Sem isso, descobrir que a chave esta errada exigiria grava-la por cima da ' +
            'que funcionava.',
    }),
    'ia.uso': rota({
        metodo: 'GET',
        caminho: '/v1/ia/uso',
        query: filtroUsoIaSchema,
        resposta: paginadoSchema(usoIaSchema),
        acesso: 'operador',
        resumo: 'O historico de chamadas de IA, com tokens e custo de cada uma.',
    }),
    'ia.resumoCusto': rota({
        metodo: 'GET',
        caminho: '/v1/ia/uso/resumo',
        query: filtroResumoCustoSchema,
        resposta: resumoCustoIaSchema,
        acesso: 'operador',
        resumo: 'Total gasto, por modelo e por dia, e o custo medio POR SITE — que e a ' +
            'pergunta da fabrica. Media por chamada mentiria: um site pode custar tres ' +
            'chamadas, e um lote de edicoes derrubaria a media sem baratear site nenhum. ' +
            'Com `lote`, recorta no envio da criacao em massa: e o gasto REAL daquele ' +
            'lote, somado do que o provedor reportou em cada chamada.',
    }),
    'ia.custoPorSite': rota({
        metodo: 'GET',
        caminho: '/v1/ia/uso/por-site',
        query: z.object({
            periodo: z.enum(['hoje', '7d', '30d', 'todos']).default('30d'),
            limite: z.coerce.number().int().min(1).max(200).default(50),
        }),
        resposta: z.array(custoPorSiteSchema),
        acesso: 'operador',
        resumo: 'Quanto cada site custou de IA, do mais caro para o mais barato.',
    }),
};
/** Erro padronizado. Todo 4xx/5xx da API tem este formato. */
export const respostaErroSchema = erroApiSchema;
/**
 * Onde, nesta rota, esta o cliente dono do recurso.
 *
 * Existe como funcao porque `rota()` devolve o tipo LITERAL de cada definicao —
 * e uma rota que nao declara `dono` simplesmente nao tem a propriedade no tipo,
 * entao `rotas[nome].dono` nao compila. Aqui o alargamento acontece uma vez,
 * num lugar so, em vez de um `as` espalhado por quem le.
 */
export function donoDaRota(nome) {
    return rotas[nome].dono;
}
/** Substitui `:param` no caminho. Erra alto se faltar — nao gera `/v1/sites/undefined`. */
export function montarCaminho(template, params) {
    return template.replace(/:([A-Za-z0-9_]+)/g, (_, nome) => {
        const valor = params?.[nome];
        if (valor === undefined || valor === null || valor === '') {
            throw new Error(`Parametro de rota ausente: ":${nome}" em "${template}". ` +
                'Isto e o bug que gerava URL com "undefined" no meio e 404 silencioso.');
        }
        return encodeURIComponent(String(valor));
    });
}
//# sourceMappingURL=rotas.js.map